import React, { useState, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../../Shared/Navbar/Navbar";
import { baseURL, headofSalesMail } from "../../../Apiservices/Api";
import { FaEdit, FaTrash } from "react-icons/fa";
import DataTable from "../../../Layout/Table/TableLayout";
import { AuthContext } from "../../../AuthContext/AuthContext";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const A_PaymentApprovals = () => {
  const { userEmail } = useContext(AuthContext);
  const { leadid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("receivables");

  /* =========================
     FETCH LEAD
  ========================== */
  const { data: leadData } = useQuery({
    queryKey: ["lead", leadid],
    queryFn: async () => {
      const res = await fetch(`${baseURL}/api/leads/${leadid}`);
      return res.json();
    },
    enabled: !!leadid,
  });

  /* =========================
     FETCH TRAVEL OPPORTUNITIES
  ========================== */
  const { data: travelOpportunities = [] } = useQuery({
    queryKey: ["travel-opportunity"],
    queryFn: async () => {
      const res = await fetch(`${baseURL}/travel-opportunity`);
      return res.json();
    },
  });

  /* =========================
     FETCH RECEIVABLES
  ========================== */
  const {
    data: receivablesRaw = [],
    isLoading,
  } = useQuery({
    queryKey: ["receivables"],
    queryFn: async () => {
      const res = await axios.get(`${baseURL}/api/receivables`);
      return res.data.reverse();
    },
  });

  /* =========================
     CALCULATE RECEIVABLES
  ========================== */
  const receivables = useMemo(() => {
    return receivablesRaw.map((item) => {
      const matchedOpportunity = travelOpportunities.find(
        (to) => to.leadid == item.leadid
      );

      const totalAmount = matchedOpportunity
        ? parseFloat(matchedOpportunity.approx_budget)
        : 0;

      const paidAmount = parseFloat(item.paid_amount || 0);
      const balanceAmount = totalAmount - paidAmount;

      return {
        ...item,
        total_amount: totalAmount,
        balance_amount: balanceAmount,
        name: item.name || leadData?.name,
      };
    });
  }, [receivablesRaw, travelOpportunities, leadData]);

  /* =========================
     UPDATE STATUS
  ========================== */
  const updateMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      return axios.put(
        `${baseURL}/api/update-status/${id}/status`,
        {
          status,
          approved_by:
            userEmail === headofSalesMail
              ? "Head of Sales"
              : "Admin",
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["receivables"]);
    },
  });

  const handleStatusChange = (id, status) => {
    alert(`Status changed to ${status}`);
    updateMutation.mutate({ id, status });
  };

  /* =========================
     DELETE
  ========================== */
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return axios.delete(
        `${baseURL}/api/delete/receivables/${id}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["receivables"]);
    },
  });

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this receivable?")) {
      deleteMutation.mutate(id);
    }
  };

  /* =========================
     TABLE COLUMNS
  ========================== */
  const tableColumns = [
    {
      Header: "S.No",
      Cell: ({ row }) => row.index + 1,
    },
    { Header: "Lead ID", accessor: "leadid" },
    { Header: "Customer Name", accessor: "name" },
    {
      Header: "Received Amount",
      accessor: "paid_amount",
      Cell: ({ value }) => `₹${value}`,
    },
    {
      Header: "Received On",
      accessor: (row) =>
        new Date(row.paid_on).toLocaleDateString("en-IN", {
          timeZone: "Asia/Kolkata",
        }),
    },
    { Header: "Remarks", accessor: "remarks" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => (
        <select
          value={row.original.status}
          onChange={(e) =>
            handleStatusChange(row.original.id, e.target.value)
          }
          style={{
            padding: "5px",
            borderRadius: "4px",
            border: "1px solid #ddd",
            backgroundColor:
              row.original.status === "Approved"
                ? "#e6f7e6"
                : "#fff",
          }}
        >
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
        </select>
      ),
    },
    {
      Header: "Action",
      Cell: ({ row }) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <FaEdit
            style={{ color: "#ff9966", cursor: "pointer" }}
            onClick={() =>
              navigate(
                `/a-edit-approval-payment/${row.original.leadid}/${row.original.id}`
              )
            }
          />
          <FaTrash
            style={{ color: "#ff9966", cursor: "pointer" }}
            onClick={() => handleDelete(row.original.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="salesViewLeadsContainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`salesViewLeads ${collapsed ? "collapsed" : ""}`}>
        <div className="payments-tabs">
          <button
            className={activeTab === "payables" ? "active" : ""}
            onClick={() => {
              setActiveTab("payables");
              navigate(`/a-approval-supplier`);
            }}
          >
            Payables
          </button>
          <button
            className={activeTab === "receivables" ? "active" : ""}
            onClick={() => setActiveTab("receivables")}
          >
            Receivables
          </button>
        </div>

        <h2>Customer Payments Approvals</h2>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <DataTable
            columns={tableColumns}
            data={receivables.filter(
              (item) => item.status === "Pending"
            )}
            pagination
            searchable
            noDataComponent={
              <div className="no-payments-found">
                No pending payments found.
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default A_PaymentApprovals;